import type { DmpChatUsage } from '../state/dmpChatState'

interface DmpChatContextUsageProps {
  isTurnActive: boolean
  usage: DmpChatUsage
}

const RING_RADIUS = 15
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

export function DmpChatContextUsage({ isTurnActive, usage }: DmpChatContextUsageProps) {
  const usagePercent =
    usage.contextWindow === undefined ? null : Math.min(100, (usage.total / usage.contextWindow) * 100)
  const ringOffset =
    usagePercent === null ? RING_CIRCUMFERENCE : RING_CIRCUMFERENCE - (usagePercent / 100) * RING_CIRCUMFERENCE
  const label = isTurnActive ? 'Prior-turn usage' : 'Completed-turn usage'
  const accessibleSummary = [
    label,
    `Input ${formatTokenCount(usage.input)} tokens`,
    `Output ${formatTokenCount(usage.output)} tokens`,
    usage.reasoning === undefined ? null : `Reasoning ${formatTokenCount(usage.reasoning)} tokens`,
    `Total ${formatTokenCount(usage.total)} tokens`,
    usage.contextWindow === undefined ? null : `Context window ${formatTokenCount(usage.contextWindow)} tokens`,
  ]
    .filter(Boolean)
    .join('. ')

  return (
    <div
      aria-label={accessibleSummary}
      className="flex min-w-0 items-center gap-2 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground"
      role="status"
    >
      <svg aria-hidden="true" className="size-8 shrink-0 -rotate-90" viewBox="0 0 36 36">
        <circle className="stroke-border" cx="18" cy="18" fill="none" r={RING_RADIUS} strokeWidth="3" />
        <circle
          className="stroke-primary transition-[stroke-dashoffset]"
          cx="18"
          cy="18"
          fill="none"
          r={RING_RADIUS}
          strokeDasharray={`${RING_CIRCUMFERENCE} ${RING_CIRCUMFERENCE}`}
          strokeDashoffset={ringOffset}
          strokeLinecap="round"
          strokeWidth="3"
        />
      </svg>
      <div className="min-w-0">
        <p className="font-medium text-foreground">{label}</p>
        <p className="truncate">
          Input {formatTokenCount(usage.input)} · Output {formatTokenCount(usage.output)}
          {usage.reasoning === undefined ? null : ` · Reasoning ${formatTokenCount(usage.reasoning)}`}
          {` · Total ${formatTokenCount(usage.total)}`}
        </p>
        {usage.contextWindow !== undefined && (
          <p className="truncate">Context window {formatTokenCount(usage.contextWindow)} tokens</p>
        )}
      </div>
    </div>
  )
}

function formatTokenCount(value: number): string {
  return value.toLocaleString()
}
